/**
 * Cloudflare Image Transformations URL builder.
 *
 * Produces `https://<host>/cdn-cgi/image/<opt>,<opt>/<source-url>` URLs so Cloudflare
 * resizes/re-encodes the image at the edge instead of the browser downloading the original.
 * Docs: https://developers.cloudflare.com/images/transform-images/transform-via-url/
 *
 * Design notes
 * - Options are joined with `,` — that is Cloudflare's syntax. (`&` happens to be tolerated
 *   today but is not documented; do not rely on it.)
 * - The transform endpoint is served from the *source image's own host*, not a fixed origin.
 *   Every `*.skin-seoul.com` host lives in the same Cloudflare zone, so `/cdn-cgi/image/` works
 *   on each of them, and using the image's own host means auth cookies (Cloudflare Access on
 *   dev/staging) and CORS behave exactly as they do for the untransformed image.
 * - `slow-connection-quality=45` lets Cloudflare serve a lighter encode when the browser sends
 *   `Save-Data: on` or a slow `ECT` hint — measured 6.1KB → 3.0KB on a 480px product image.
 * - `onerror=redirect` is always added: if Cloudflare cannot transform (feature disabled,
 *   origin 4xx, unsupported format) it 302s to the original instead of showing a broken image.
 * - Hosts outside the allowlist are returned untouched — Cloudflare refuses to resize
 *   images from arbitrary third-party origins.
 */

export type CloudflareImageFormat = 'auto' | 'avif' | 'webp' | 'jpeg' | 'baseline-jpeg';
export type CloudflareImageFit = 'scale-down' | 'contain' | 'cover' | 'crop' | 'pad';

export type CloudflareImageOptions = {
  /** Target width in CSS pixels (Cloudflare never upscales with `scale-down`). */
  width?: number;
  height?: number;
  /** 1–100. Cloudflare default is 85; 75 is a good web default. */
  quality?: number;
  /** `auto` serves AVIF/WebP based on the `Accept` header. */
  format?: CloudflareImageFormat;
  fit?: CloudflareImageFit;
  /** Device pixel ratio multiplier for width/height. */
  dpr?: number;
  /** Strip EXIF. Default `none` (smallest output). */
  metadata?: 'copyright' | 'keep' | 'none';
  /**
   * Quality used instead of `quality` when the browser signals a slow connection
   * (`Save-Data: on` or `ECT: slow-2g|2g|3g`). Default 45 (~50% smaller than q75).
   */
  'slow-connection-quality'?: number;
};

export type BuildCloudflareImageUrlConfig = {
  /**
   * Predicate deciding whether a hostname is eligible for transformation.
   * Defaults to any host ending in `skin-seoul.com`.
   */
  isAllowedHost?: (hostname: string) => boolean;
  /**
   * Host that serves the `/cdn-cgi/image/` endpoint. Defaults to the source image's own host.
   * Only override when a host is not proxied through Cloudflare.
   */
  transformHost?: string;
};

const DEFAULT_OPTIONS: Required<
  Pick<CloudflareImageOptions, 'quality' | 'format' | 'fit' | 'metadata' | 'slow-connection-quality'>
> = {
  quality: 75,
  format: 'auto',
  fit: 'scale-down',
  metadata: 'none',
  'slow-connection-quality': 45,
};

export const isSkinSeoulHost = (hostname: string): boolean =>
  hostname === 'skin-seoul.com' || hostname.endsWith('.skin-seoul.com');

const CDN_CGI_MARKER = '/cdn-cgi/image/';

/**
 * Serialize options in Cloudflare's `key=value,key=value` form.
 * Undefined values are dropped *before* merging so they cannot shadow a default
 * (`next/image` passes `quality: undefined` when the prop is omitted).
 */
export const serializeCloudflareImageOptions = (options: CloudflareImageOptions): string => {
  const explicit = Object.entries(options).filter(([, value]) => value !== undefined && value !== null);
  const merged: Record<string, string | number> = {
    ...DEFAULT_OPTIONS,
    ...Object.fromEntries(explicit),
    onerror: 'redirect',
  };
  return Object.entries(merged)
    .map(([key, value]) => `${key}=${value}`)
    .join(',');
};

/**
 * Build a Cloudflare-transformed URL for `src`.
 * Returns `src` unchanged when it is relative, unparsable, already transformed,
 * or hosted somewhere Cloudflare cannot fetch from.
 */
export const buildCloudflareImageUrl = (
  src: string,
  options: CloudflareImageOptions = {},
  config: BuildCloudflareImageUrlConfig = {},
): string => {
  if (typeof src !== 'string' || src.length === 0) {
    return src;
  }
  if (!/^https?:\/\//i.test(src)) {
    return src;
  }
  if (src.includes(CDN_CGI_MARKER)) {
    return src;
  }

  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return src;
  }

  const isAllowedHost = config.isAllowedHost ?? isSkinSeoulHost;
  if (!isAllowedHost(url.hostname)) {
    return src;
  }

  const transformHost = config.transformHost ?? url.host;
  return `https://${transformHost}${CDN_CGI_MARKER}${serializeCloudflareImageOptions(options)}/${src}`;
};

/**
 * Build a `srcset` string for the given candidate widths.
 * Non-transformable sources yield an empty string so callers can omit the attribute.
 */
export const buildCloudflareImageSrcSet = (
  src: string,
  widths: readonly number[],
  options: Omit<CloudflareImageOptions, 'width'> = {},
  config: BuildCloudflareImageUrlConfig = {},
): string => {
  const first = buildCloudflareImageUrl(src, { ...options, width: widths[0] }, config);
  if (first === src) {
    return '';
  }
  return widths.map((width) => `${buildCloudflareImageUrl(src, { ...options, width }, config)} ${width}w`).join(', ');
};
